import axios from "axios";
import { api } from "./apiService";

const configuredBaseUrl =
  process.env.REACT_APP_ABHA_API_BASE_URL ||
  "https://bhims.pranamm.ai/abdm/api/abha";
const BASE_URL = `${configuredBaseUrl.replace(/\/+$/, "")}/`;
const m2_BASE_URL = process.env.REACT_APP_M2;

const validationError = (message) => {
  throw new Error(message);
};

const requireDigits = (value, length, label) => {
  const rawValue = String(value || "").trim();
  if (!/^[\d\s-]+$/.test(rawValue))
    validationError(`${label} contains invalid characters.`);
  const normalized = rawValue.replace(/[\s-]/g, "");
  if (normalized.length !== length)
    validationError(`${label} must contain exactly ${length} digits.`);
  return normalized;
};

const requireOtp = (value) => requireDigits(value, 6, "OTP");

const requireTransactionId = (value) => {
  if (!String(value || "").trim())
    validationError("Transaction ID is required.");
  return String(value).trim();
};

// WASA-08 Note: ABDM RSA public key used for client-side encryption.
// To ensure compliance and prevent persistent exposure, keys are strictly kept in module memory
// and never persisted to browser localStorage or sessionStorage.
export const encryptString = async (plainText) => {
  try {
    if (!String(plainText || "").trim())
      validationError("Value to encrypt is required.");
    // Accept base64 public key directly from environment (supports standard and legacy alias)
    const keyData =
      process.env.REACT_APP_ABDM_PUBLIC_KEY ||
      process.env.REACT_APP_ABMD_PUBLIC_KEY;

    // Validate that the public key exists
    if (!keyData) {
      console.error("Public key not found in environment variables");
      throw new Error("Public key not configured");
    }

    // Validate that the public key is properly base64 encoded
    try {
      // Test if keyData is valid base64
      atob(keyData);
    } catch (base64Error) {
      console.error("Invalid base64 public key:", base64Error);
      throw new Error("Invalid public key format");
    }

    const binaryDer = Uint8Array.from(atob(keyData), (c) => c.charCodeAt(0));
    const publicKey = await window.crypto.subtle.importKey(
      "spki",
      binaryDer.buffer,
      {
        name: "RSA-OAEP",
        hash: { name: "SHA-1" },
      },
      true,
      ["encrypt"],
    );
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(plainText);
    const encryptedData = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      publicKey,
      encodedData,
    );
    const encryptedBase64 = btoa(
      String.fromCharCode(...new Uint8Array(encryptedData)),
    );
    return encryptedBase64;
  } catch (err) {
    console.error("Encryption Error:", err);
    throw new Error("Failed to encrypt string.");
  }
};

export const getAccessToken = async () => {
  try {
    const url = `${BASE_URL}token`;
    const response = await axios.post(
      url,
      {},
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    const token =
      response.data?.accessToken || response.data?.token || response.data;

    if (typeof token !== "string" || !token.trim()) {
      throw new Error("ABDM access token was not returned by the server.");
    }

    return token.trim();
  } catch (error) {
    console.error("Session Creation Error:", error);
    throw new Error("Failed to create session.");
  }
};

export const sendAadharOtp = async (aadhar, accessToken) => {
  try {
    aadhar = requireDigits(aadhar, 12, "Aadhaar number");
    // Ensure accessToken is present
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedAadhar = await encryptString(aadhar);
    const url = `${BASE_URL}enrollment/aadhar/send-otp`;
    const body = { loginId: encryptedAadhar };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Send OTP Error:", error);
    throw new Error("Failed to send OTP.");
  }
};

export const verifyAadharOtp = async (txnId, otp, mobile, accessToken) => {
  try {
    txnId = requireTransactionId(txnId);
    otp = requireOtp(otp);
    mobile = requireDigits(mobile, 10, "Mobile number");
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedOtp = await encryptString(otp);

    const url = `${BASE_URL}enrollment/aadhar/verify-otp`;
    const body = {
      txnId,
      otpValue: encryptedOtp,
      mobile,
    };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Verify OTP Error:", error);
    throw new Error("Failed to verify OTP.");
  }
};

// Send OTP for mobile login
export const sendMobileOtp = async (mobile, accessToken) => {
  try {
    mobile = requireDigits(mobile, 10, "Mobile number");
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedMobile = await encryptString(mobile);
    const url = `${BASE_URL}login/mobile/send-otp`;
    const body = { mobileNumber: encryptedMobile };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Send Mobile OTP Error:", error);
    throw new Error("Failed to send mobile OTP.");
  }
};

// Verify OTP for mobile login
export const verifyMobileOtp = async (txnId, otp, accessToken) => {
  try {
    txnId = requireTransactionId(txnId);
    otp = requireOtp(otp);
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedOtp = await encryptString(otp);
    const url = `${BASE_URL}login/mobile/verify-otp`;
    const body = {
      txnId,
      otp: encryptedOtp,
    };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Verify Mobile OTP Error:", error);
    throw new Error("Failed to verify mobile OTP.");
  }
};

// Send OTP for abha number login
export const loginAbhaSendOtp = async (abhaNumber, accessToken) => {
  try {
    abhaNumber = requireDigits(abhaNumber, 14, "ABHA number");
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedAbha = await encryptString(abhaNumber);
    const url = `${BASE_URL}login/abha/number/send-otp`;
    const body = { abhaNumber: encryptedAbha };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Send Abha Number Login OTP Error:", error);
    throw new Error("Failed to send abha number login OTP.");
  }
};

// Verify OTP for abha number login
export const loginAbhaVerifyOtp = async (txnId, otp, accessToken) => {
  try {
    txnId = requireTransactionId(txnId);
    otp = requireOtp(otp);
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedOtp = await encryptString(otp);
    const url = `${BASE_URL}login/abha/number/verify-otp`;
    const body = {
      txnId,
      otp: encryptedOtp,
    };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        // accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Verify Abha Number Login OTP Error:", error);
    throw new Error("Failed to verify abha number login OTP.");
  }
};
// Send OTP for aadhar login
export const loginAadharSendOtp = async (aadhar, accessToken) => {
  try {
    aadhar = requireDigits(aadhar, 12, "Aadhaar number");
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedAadhar = await encryptString(aadhar);
    const url = `${BASE_URL}login/aadhar/send-otp`;
    const body = { aadharNumber: encryptedAadhar };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Send Aadhar Login OTP Error:", error);
    throw new Error("Failed to send aadhar login OTP.");
  }
};

// Verify OTP for aadhar login
export const loginAadharVerifyOtp = async (txnId, otp, accessToken) => {
  try {
    txnId = requireTransactionId(txnId);
    otp = requireOtp(otp);
    if (!accessToken) {
      accessToken = await getAccessToken();
    }
    const encryptedOtp = await encryptString(otp);
    const url = `${BASE_URL}login/aadhar/verify-otp`;
    const body = {
      txnId,
      otp: encryptedOtp,
    };
    const response = await axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accesstoken: accessToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Verify Aadhar Login OTP Error:", error);
    throw new Error("Failed to verify aadhar login OTP.");
  }
};

// Get ABHA profile data after OTP verification
export const getAbhaProfile = async (xToken) => {
  const accessToken = await getAccessToken();
  try {
    const url = `${BASE_URL}profile`;
    const response = await axios.get(url, {
      headers: {
        accesstoken: accessToken,
        xtoken: xToken,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Get ABHA Profile Error:", error);
    console.error("Error details:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
      },
    });
    throw new Error("Failed to get ABHA profile data.");
  }
};

export const verifyUser = async (accessToken, x_token, abhano, txnId) => {
  const url = `${BASE_URL}account/verify`;
  const param = {
    txnId: txnId,
    abhanumber: abhano,
  };
  const header = {
    headers: {
      "Content-Type": "application/json",
      "t-token": `${x_token}`,
      // accesstoken: `${accessToken}`,
    },
  };
  const response = await axios.post(url, param, header);
  return response.data;
};

// Register patient with ABHA profile and all form data
export const registerAbhaProfile = async (registrationData) => {
  try {
    const url = `${m2_BASE_URL}registration`;

    // Extract data from the registration payload
    const { formFields, coreInfo, hiTypes = [] } = registrationData;

    // Create the base body structure according to your required format
    const body = {
      abhaNumber: coreInfo.abhaNumber || "",
      abhaAddress: coreInfo.abhaAddress || "",
      name: coreInfo.fullName || "",
      gender:
        coreInfo.gender === "Male"
          ? "M"
          : coreInfo.gender === "Female"
            ? "F"
            : coreInfo.gender || "",
      yearOfBirth:
        parseInt(coreInfo.yearOfBirth) || new Date().getFullYear() - 30,
    };

    // Handle multiple HiTypes (for assessment.js vitals screen - 3 HiTypes)
    if (hiTypes && hiTypes.length > 0) {
      hiTypes.forEach((hiTypeData) => {
        const { hiType, displayName, fields } = hiTypeData;

        // Create careContexts object for this HiType
        const careContexts = {};
        let fieldCount = 0;

        if (fields && Array.isArray(fields)) {
          fields.forEach((field) => {
            if (
              field.fieldName &&
              field.fieldValue &&
              field.fieldValue !== ""
            ) {
              careContexts[field.fieldName] = field.fieldValue;
              fieldCount++;
            }
          });
        }

        // Add this HiType to the body
        body[hiType] = {
          referenceNumber: coreInfo.fullName || "", // user name
          display: displayName || "Apollo_prescription", // display name
          careContexts: [careContexts], // array with object containing field name-value pairs
          hiType: hiType,
          count: fieldCount, // total number of fields filled by users
        };
      });
    } else {
      // Handle single HiType (for other places like Patients.js, Billing.js)
      const careContexts = {};
      let fieldCount = 0;

      if (formFields && Array.isArray(formFields)) {
        formFields.forEach((field) => {
          if (field.fieldName && field.fieldValue && field.fieldValue !== "") {
            careContexts[field.fieldName] = field.fieldValue;
            fieldCount++;
          }
        });
      }

      // Default to HealthDocumentRecord for backward compatibility
      const hiType = coreInfo.hiType;
      const displayName = coreInfo.displayName || "Apollo_prescription";

      body[hiType] = {
        referenceNumber: coreInfo.fullName || "", // user name
        display: displayName, // display name
        careContexts: [careContexts], // array with object containing field name-value pairs
        hiType: hiType,
        count: fieldCount, // total number of fields filled by users
      };
    }

    const response = await axios.post(url, body, {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    if (!error.response) {
      throw new Error("Network error: Cannot connect to the server.");
    }

    // Handle different status codes
    let errorMessage = "Failed to register patient.";

    if (error.response.status === 400) {
      if (error.response.data && typeof error.response.data === "object") {
        // Handle nested error object structure
        if (error.response.data.error && error.response.data.error.error) {
          const errorObj = error.response.data.error.error;
          errorMessage =
            errorObj.message || errorObj.code || JSON.stringify(errorObj);
        } else {
          // Try to extract meaningful error message
          errorMessage =
            error.response.data.message ||
            error.response.data.error ||
            error.response.data.detail ||
            JSON.stringify(error.response.data);
        }
      } else if (error.response.data) {
        errorMessage = error.response.data;
      } else {
        errorMessage =
          "Bad Request: Please check if all required fields are provided correctly.";
      }
    } else if (error.response.status === 401) {
      errorMessage = "Unauthorized: Authentication required.";
    } else if (error.response.status === 403) {
      errorMessage = "Forbidden: Access denied.";
    } else if (error.response.status === 404) {
      errorMessage = "Not Found: Registration endpoint not available.";
    } else if (error.response.status === 500) {
      errorMessage = "Server Error: Please try again later.";
    }

    throw new Error(errorMessage);
  }
};

export const addNewPatient = async (data) => {
  try {
    const url = `/patient/register`;

    const response = await api.post(url, data);

    return response.data;
  } catch (error) {
    console.error("Add New Patient Error:", error);
    if (error.code === "INPUT_VALIDATION_ERROR") {
      throw error;
    }
    if (!error.response) {
      throw new Error("Network error: Cannot connect to the server.");
    }
    const errorMessage =
      error.response.data?.message ||
      error.response.data?.error ||
      "Failed to register patient.";
    throw new Error(errorMessage);
  }
};

export const mergePatientAbha = async (data) => {
  try {
    const url = `/patient/link-to-abha`;

    const response = await api.post(url, data);

    return response.data;
  } catch (error) {
    console.error("Merge Patient ABHA Error:", error);
    throw new Error(
      error.response?.data?.message || "Failed to merge ABHA details",
    );
  }
};
